// src/store/slices/servicesSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Service, ServiceFormData } from '../../types/service';
import api from '../../api/apiClient';

// Removed mock services as we're now using real API data

// API error interface
interface ApiError {
  status?: number;
  message?: string;
  data?: any;
  url?: string;
}

interface ServicesState {
  services: Service[];
  currentService: Service | null;
  isLoading: boolean;
  error: string | ApiError | null;
}

const initialState: ServicesState = {
  services: [],
  currentService: null,
  isLoading: false,
  error: null,
};

// Request deduplication - prevent multiple simultaneous calls
let pendingServicesRequest: Promise<any> | null = null;

// Async thunks for service management
export const fetchStylistServices = createAsyncThunk(
  'services/fetchAll',
  async (stylistId: string, { rejectWithValue }) => {
    // If there's already a pending request, return it instead of making a new one
    if (pendingServicesRequest) {
      try {
        return await pendingServicesRequest;
      } catch (err) {
        // Clear pending request on error so next call can retry
        pendingServicesRequest = null;
        throw err;
      }
    }

    // Create new request
    pendingServicesRequest = (async () => {
      try {
        // stylistId parameter is a dummy placeholder to match mobile app pattern
        // The backend API will identify the stylist from the auth token
        console.log(`Attempting to fetch services (auth token will identify the stylist)`);
        console.log(`Will try multiple endpoints to fetch services`);
        
        // Make the API call to get stylist services
        const response = await api.stylist.getServices(stylistId);
        
        // Log the response for debugging
        console.log('Fetched services response:', response);
        
        // Parse API response based on expected structure
        if (response.data) {
          // If the data contains a 'data' property that's an array (common pattern in APIs)
          if (response.data.data && Array.isArray(response.data.data)) {
            console.log('Using response.data.data array');
            
            // Convert the mobile app ServiceWithDetails format to the web Service format if necessary
            return response.data.data.map((item: any) => {
              // Check if this is in the mobile app format (has service property)
              if (item.service) {
                return {
                  _id: item._id,
                  stylistId: item.stylistId,
                  name: item.service.name,
                  description: {
                    short: item.service.description?.short || '',
                    detailed: item.service.description?.full || ''
                  },
                  category: item.service.category,
                  subcategory: item.service.subcategory || item.service.category || '',
                  basePrice: item.service.basePrice,
                  baseDuration: item.service.baseDuration,
                  variations: item.variations || [],
                  isActive: item.isEnabled !== undefined ? item.isEnabled : (item.availability !== undefined ? item.availability : true),
                  createdAt: item.createdAt || new Date().toISOString(),
                  updatedAt: item.updatedAt || new Date().toISOString()
                };
              }
              // If already in the correct format, return as is
              return item;
            });
          }
          
          // If response.data itself is an array
          if (Array.isArray(response.data)) {
            console.log('Using response.data array');
            return response.data;
          }
          
          // If it's an object with a services property that is an array
          if (response.data.services && Array.isArray(response.data.services)) {
            console.log('Using response.data.services array');
            return response.data.services;
          }
          
          console.warn('Unexpected API response structure:', response.data);
          return [];
        }
        
        console.warn('Empty API response');
        return [];
      } catch (error: any) {
        console.error('Error fetching services:', error);
        console.error('Error details:', {
          status: error.status,
          message: error.data?.message || error.message || 'Failed to fetch services',
          data: error.data,
          url: `/stylist-services/stylist/${stylistId}`
        });
        
        const errorDetails = {
          status: error.status,
          message: error.data?.message || error.message || 'Failed to fetch services',
          data: error.data,
          url: `/stylist-services/stylist/${stylistId}`, // The endpoint that was called
        };
        throw errorDetails;
      } finally {
        // Clear pending request after completion
        pendingServicesRequest = null;
      }
    })();

    try {
      return await pendingServicesRequest;
    } catch (err: any) {
      return rejectWithValue(err);
    }
  }
);

export const fetchServiceById = createAsyncThunk(
  'services/fetchById',
  async (serviceId: string, { rejectWithValue }) => {
    try {
      console.log(`Fetching service with ID: ${serviceId}`);
      // Make the API call to get a specific service
      const response = await api.stylist.getServiceById(serviceId);
      console.log('Fetched service by ID response:', response);
      
      // Handle different response formats
      if (response.data) {
        if (response.data.data) {
          return response.data.data;
        }
        return response.data;
      }
      
      throw new Error('API returned empty response');
    } catch (error: any) {
      console.error('Error fetching service by ID:', error);
      const errorDetails = {
        status: error.status,
        message: error.data?.message || error.message || 'Failed to fetch service',
        data: error.data,
        url: `/stylist-services/${serviceId}`,
      };
      return rejectWithValue(errorDetails);
    }
  }
);

export const createService = createAsyncThunk(
  'services/create',
  async ({ 
    stylistId,
    serviceData 
  }: { 
    stylistId: string, 
    serviceData: ServiceFormData 
  }, { rejectWithValue }) => {
    try {
      // stylistId parameter is a dummy placeholder to match mobile app pattern
      // The backend API will identify the stylist from the auth token
      console.log(`Creating service (auth token will identify the stylist)`);
      console.log(`Service data being sent:`, JSON.stringify(serviceData, null, 2));
      
      // Make the API call to create a new service
      // Using the singular "stylist" endpoint: /stylist/services
      const response = await api.stylist.createService(stylistId, serviceData);
      
      // Log response for debugging
      console.log('Create service response:', response);
      console.log('Response data structure:', JSON.stringify(response.data, null, 2));
      
      // Handle API response
      if (response.data) {
        // If response has a nested data property (standard API format)
        if (response.data.data) {
          console.log('Using nested data format from response');
          return response.data.data;
        }
        
        // If data is directly in the response
        console.log('Using direct data format from response');
        return response.data;
      }
      
      // If API returned a success but empty response (unlikely)
      console.error('API returned empty response');
      throw new Error('API returned empty response');
    } catch (error: any) {
      const data = error.response?.data || error.data;
      const errorMessage =
        data?.message ||
        data?.error ||
        (typeof error.message === 'string' ? error.message : null) ||
        'We couldn’t create the service. Please check the form and try again.';
      return rejectWithValue({
        status: error.response?.status ?? error.status,
        message: errorMessage,
        url: `/stylist-services`
      });
    }
  }
);

export const updateService = createAsyncThunk(
  'services/update',
  async ({ 
    serviceId, 
    serviceData 
  }: { 
    serviceId: string, 
    serviceData: Partial<ServiceFormData> 
  }, { rejectWithValue }) => {
    try {
      console.log(`Updating service with ID: ${serviceId}`);
      // Backend expects isEnabled; web form uses isActive
      const payload = {
        ...serviceData,
        ...(serviceData.isActive !== undefined && { isEnabled: serviceData.isActive })
      };
      console.log(`Service data being sent:`, JSON.stringify(payload, null, 2));
      
      // Make the API call to update the service
      const response = await api.stylist.updateService(serviceId, payload);
      console.log('Update service response:', response);
      console.log('Response data structure:', JSON.stringify(response.data, null, 2));
      
      // Handle different response formats
      if (response.data) {
        // If response has nested data property
        if (response.data.data) {
          console.log('Using nested data format from response');
          const serviceData = response.data.data;
          
          // Check if this is in the mobile app format (has service property)
          if (serviceData.service) {
            console.log('Converting mobile app format to web client format');
            return {
              _id: serviceData._id,
              stylistId: serviceData.stylistId,
              name: serviceData.service.name,
              description: {
                short: serviceData.service.description?.short || '',
                detailed: serviceData.service.description?.full || ''
              },
              category: serviceData.service.category,
              basePrice: serviceData.service.basePrice,
              baseDuration: serviceData.service.baseDuration,
              variations: serviceData.variations || [],
              isActive: serviceData.isEnabled !== undefined ? serviceData.isEnabled : (serviceData.availability !== undefined ? serviceData.availability : true),
              createdAt: serviceData.createdAt || new Date().toISOString(),
              updatedAt: serviceData.updatedAt || new Date().toISOString()
            };
          }
          
          return response.data.data;
        }
        
        console.log('Using direct data format from response');
        return response.data;
      }
      
      console.warn('Empty API response for service update');
      throw new Error('API returned empty response');
    } catch (error: any) {
      const data = error.response?.data || error.data;
      const errorMessage =
        data?.message ||
        data?.error ||
        (typeof error.message === 'string' ? error.message : null) ||
        'We couldn’t save your changes. Please check your connection and try again.';
      return rejectWithValue({
        status: error.response?.status ?? error.status,
        message: errorMessage,
        url: `/stylist/services/${serviceId}`
      });
    }
  }
);

export const toggleServiceActive = createAsyncThunk(
  'services/toggleActive',
  async (serviceId: string, { rejectWithValue, getState }) => {
    try {
      // Get the current state
      const state = getState() as { services: ServicesState };
      const service = state.services.services.find(s => s._id === serviceId);
      
      if (!service) {
        throw new Error('Service not found');
      }
      
      console.log(`Toggling service active status: ${serviceId}, setting to: ${!service.isActive}`);
      
      // Make the API call to toggle the service active status
      const response = await api.stylist.toggleServiceActive(serviceId, !service.isActive);
      console.log('Toggle service active response:', response);
      
      // Handle different response formats
      if (response.data) {
        // If response has nested data property
        if (response.data.data) {
          const serviceData = response.data.data;
          
          // Check if this is in the mobile app format (has service property)
          if (serviceData.service) {
            return {
              _id: serviceData._id,
              stylistId: serviceData.stylistId,
              name: serviceData.service.name,
              description: {
                short: serviceData.service.description?.short || '',
                detailed: serviceData.service.description?.full || ''
              },
              category: serviceData.service.category,
              basePrice: serviceData.service.basePrice,
              baseDuration: serviceData.service.baseDuration,
              variations: serviceData.variations || [],
              isActive: serviceData.availability !== undefined ? serviceData.availability : !service.isActive,
              createdAt: serviceData.createdAt || service.createdAt,
              updatedAt: serviceData.updatedAt || new Date().toISOString()
            };
          }
          
          return response.data.data;
        }
        
        return response.data;
      }
      
      // If no proper response, create a toggled version of the existing service
      return {
        ...service,
        isActive: !service.isActive,
        updatedAt: new Date().toISOString()
      };
    } catch (error: any) {
      console.error('Error toggling service active status:', error);
      const errorDetails = {
        status: error.status,
        message: error.data?.message || error.message || 'Failed to toggle service status',
        data: error.data,
        url: `/stylist-services/${serviceId}/active`, // The endpoint that was called
      };
      return rejectWithValue(errorDetails);
    }
  }
);

export const deleteService = createAsyncThunk(
  'services/delete',
  async (serviceId: string, { rejectWithValue }) => {
    try {
      console.log(`Deleting service with ID: ${serviceId}`);
      
      // Make the API call to delete the service
      const response = await api.stylist.deleteService(serviceId);
      console.log('Delete service response:', response);
      
      // For delete operations, we typically just need the ID to remove it from the store
      return serviceId;
    } catch (error: any) {
      console.error('Error deleting service:', error);
      const errorDetails = {
        status: error.status,
        message: error.data?.message || error.message || 'Failed to delete service',
        data: error.data,
        url: `/stylist-services/${serviceId}`, // The endpoint that was called
      };
      return rejectWithValue(errorDetails);
    }
  }
);

const servicesSlice = createSlice({
  name: 'services',
  initialState,
  reducers: {
    clearCurrentService: (state) => {
      state.currentService = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchStylistServices
      .addCase(fetchStylistServices.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchStylistServices.fulfilled, (state, action: PayloadAction<Service[]>) => {
        state.isLoading = false;
        state.services = action.payload;
      })
      .addCase(fetchStylistServices.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as any;
      })
      
      // fetchServiceById
      .addCase(fetchServiceById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchServiceById.fulfilled, (state, action: PayloadAction<Service>) => {
        state.isLoading = false;
        state.currentService = action.payload;
      })
      .addCase(fetchServiceById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as any;
      })
      
      // createService
      .addCase(createService.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createService.fulfilled, (state, action: PayloadAction<Service>) => {
        state.isLoading = false;
        state.services.push(action.payload);
        state.currentService = action.payload;
      })
      .addCase(createService.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as any;
      })
      
      // updateService
      .addCase(updateService.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateService.fulfilled, (state, action: PayloadAction<Service>) => {
        state.isLoading = false;
        const index = state.services.findIndex(service => service._id === action.payload._id);
        if (index !== -1) {
          state.services[index] = action.payload;
        }
        state.currentService = action.payload;
      })
      .addCase(updateService.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as any;
      })
      
      // toggleServiceActive
      .addCase(toggleServiceActive.fulfilled, (state, action: PayloadAction<Service>) => {
        const index = state.services.findIndex(service => service._id === action.payload._id);
        if (index !== -1) {
          state.services[index] = action.payload;
        }
        if (state.currentService && state.currentService._id === action.payload._id) {
          state.currentService = action.payload;
        }
      })
      
      // deleteService
      .addCase(deleteService.fulfilled, (state, action: PayloadAction<string>) => {
        state.services = state.services.filter(service => service._id !== action.payload);
        if (state.currentService && state.currentService._id === action.payload) {
          state.currentService = null;
        }
      });
  },
});

export const { clearCurrentService } = servicesSlice.actions;

export default servicesSlice.reducer;